import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { usePermissionsReadModel } from './use-permissions-read-model.js';
import { usePermissionsWrite } from './use-permissions-write.js';
import type { Permission } from './use-permissions-read-model.js';

const CONTROL: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(7,11,18,0.72)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  display: 'block', color: 'rgba(255,255,255,0.55)', fontSize: 12,
  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
};

const ALL_MODULES = [
  'ops_dashboard', 'daily_entry', 'scorecard', 'directory',
  'scheduler', 'fleet', 'finance', 'permissions', 'onboarding',
  'safety_meetings',
];

export default function PermissionsPanel() {
  const [gateCode, setGateCode] = useState('');
  const unlocked = gateCode === '1337';

  const [searchEmail, setSearchEmail] = useState('');
  const [showGrant, setShowGrant] = useState(false);
  const [grantModule, setGrantModule] = useState('');
  const [grantCanGrant, setGrantCanGrant] = useState(false);
  const [grantNotes, setGrantNotes] = useState('');
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const {
    targetEmail,
    active,
    revoked,
    loading,
    error: readError,
    loaded,
    loadPermissions,
    updateFromResponse,
  } = usePermissionsReadModel();

  const {
    grantPermission,
    revokePermission,
    revokeAll,
    submitting,
    error: writeError,
    clearError,
  } = usePermissionsWrite(updateFromResponse);

  async function handleGrant() {
    if (!grantModule || !targetEmail) return;
    const success = await grantPermission(targetEmail, grantModule, grantCanGrant, grantNotes || undefined);
    if (success) {
      setShowGrant(false);
      setGrantModule('');
      setGrantCanGrant(false);
      setGrantNotes('');
    }
  }

  async function handleRevokeAll() {
    if (!targetEmail) return;
    const success = await revokeAll(targetEmail);
    if (success) setConfirmRevokeAll(false);
  }

  const displayError = writeError || readError;

  if (!unlocked) {
    return (
      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 10 }}>
          Permissions management
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 200 }}>
            <label style={LABEL}>Authorization code</label>
            <input
              type="password"
              value={gateCode}
              onChange={(e) => setGateCode(e.target.value)}
              placeholder="Enter code"
              style={CONTROL}
              autoComplete="off"
            />
          </div>
          <div style={{ color: C.textMuted, fontSize: 12, paddingBottom: 12 }}>
            Keymaker access required
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
        Permissions management
      </div>

      {/* Search by email */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL}>Employee email</label>
          <input
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && searchEmail.includes('@')) loadPermissions(searchEmail); }}
            placeholder="name@prestigeworlddrive.com"
            style={CONTROL}
          />
        </div>
        <button
          type="button"
          onClick={() => loadPermissions(searchEmail)}
          disabled={!searchEmail.includes('@') || loading}
          style={{
            padding: '10px 20px', borderRadius: 8,
            background: searchEmail.includes('@') && !loading ? C.gold : 'rgba(255,255,255,0.06)',
            color: searchEmail.includes('@') && !loading ? '#111' : 'rgba(255,255,255,0.2)',
            border: 'none', fontWeight: 700, fontSize: 13,
            cursor: searchEmail.includes('@') && !loading ? 'pointer' : 'not-allowed',
          }}
        >
          {loading ? 'Loading...' : 'Look up'}
        </button>
      </div>

      {displayError && (
        <div style={{ color: C.red, fontSize: 13, marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)' }}>
          {displayError}
        </div>
      )}

      {loaded && targetEmail && (
        <>
          {/* Active permissions */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>Active permissions for {targetEmail}</div>
              <button
                type="button"
                onClick={() => { setShowGrant(!showGrant); clearError(); }}
                style={{ background: 'none', border: 'none', color: C.gold, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                {showGrant ? 'Cancel' : '+ Grant'}
              </button>
            </div>

            {active.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 12 }}>No active permissions.</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {active.map((perm: Permission) => (
                  <div key={perm.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                        {perm.module}
                        {perm.can_grant && <span style={{ color: C.gold, fontSize: 11, marginLeft: 6 }}>keymaker</span>}
                      </div>
                      <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>
                        by {perm.granted_by_email || '—'} · {perm.granted_at ? String(perm.granted_at).slice(0, 10) : '—'}
                        {perm.notes ? ` · ${perm.notes}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => revokePermission(targetEmail, perm.module)}
                      disabled={submitting}
                      style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.red}`, background: 'transparent', color: C.red, fontSize: 11, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.5 : 1 }}
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grant form */}
          {showGrant && (
            <div style={{ padding: 14, borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.borderSoft}`, marginBottom: 16 }}>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr', marginBottom: 10 }}>
                <div>
                  <label style={LABEL}>Module</label>
                  <select value={grantModule} onChange={(e) => setGrantModule(e.target.value)} style={CONTROL}>
                    <option value="">Select module...</option>
                    {ALL_MODULES.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LABEL}>Notes (optional)</label>
                  <input type="text" value={grantNotes} onChange={(e) => setGrantNotes(e.target.value)} placeholder="Reason..." style={CONTROL} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.text, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={grantCanGrant} onChange={(e) => setGrantCanGrant(e.target.checked)} />
                Grant keymaker (can_grant) authority on this module
              </label>
              <button
                type="button"
                onClick={handleGrant}
                disabled={!grantModule || submitting}
                style={{
                  padding: '8px 18px', borderRadius: 8,
                  background: grantModule && !submitting ? C.green : 'rgba(255,255,255,0.06)',
                  color: grantModule && !submitting ? '#111' : 'rgba(255,255,255,0.2)',
                  border: 'none', fontWeight: 700, fontSize: 13,
                  cursor: grantModule && !submitting ? 'pointer' : 'not-allowed',
                }}
              >
                {submitting ? 'Granting...' : 'Grant permission'}
              </button>
            </div>
          )}

          {/* Revoke all */}
          {active.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {!confirmRevokeAll ? (
                <button
                  type="button"
                  onClick={() => setConfirmRevokeAll(true)}
                  style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid rgba(255,107,107,0.3)`, background: 'transparent', color: C.red, fontSize: 12, cursor: 'pointer' }}
                >
                  Revoke all permissions
                </button>
              ) : (
                <div style={{ padding: 12, borderRadius: 8, background: 'rgba(255,107,107,0.08)', border: `1px solid ${C.red}` }}>
                  <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>This will revoke ALL active permissions for {targetEmail}.</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={handleRevokeAll}
                      disabled={submitting}
                      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.red, color: '#fff', fontSize: 12, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer' }}
                    >
                      {submitting ? 'Revoking...' : 'Confirm revoke all'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRevokeAll(false)}
                      style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 12, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Revoked history */}
          {revoked.length > 0 && (
            <div>
              <div style={{ color: C.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Revoked ({revoked.length})
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                {revoked.slice(0, 10).map((perm: Permission) => (
                  <div key={perm.id} style={{ padding: 8, borderRadius: 6, background: C.panel, fontSize: 12, color: C.textMuted }}>
                    {perm.module} · revoked {perm.revoked_at ? String(perm.revoked_at).slice(0, 10) : '—'} by {perm.revoked_by_email || '—'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
